// Event photography pages (October 2026): landing pages for Photohawk Studio by sport (running, triathlon, cycling
// sportives, trail and ultra, equestrian, obstacle runs) and for organisers (offering photos, sponsor-paid free
// photos, results, marketing, choosing a partner, GDPR and face search). Live everywhere, at
// /event-photography/<slug>, with the hub at /event-photography.
//
// One Markdown file per page, src/content/eventPhotography/<slug>.md, in the blog's format (src/content/blog: the
// same front matter, body blocks and checks), plus `group: sports | organisers` (which part of the hub lists it).
// Every page has a hero image, an FAQ and 3+ internal links: src/content/eventPhotography/pages.test.tsx.

import { buildPost, getPost, type BlogPost } from "@/content/blog";

export const EVENT_PHOTOGRAPHY_PATH = "/event-photography";

export const EVENT_GROUPS = {
  sports: { title: "By sport", intro: "How Photohawk Studio fits the way each sport is photographed, searched and sold." },
  organisers: { title: "For organisers", intro: "Offering photos to participants: free or paid, in your results, in your emails, and with privacy done properly." },
} as const;
export type EventGroup = keyof typeof EVENT_GROUPS;

const FILES = import.meta.glob("./*.md", { query: "?raw", import: "default", eager: true }) as Record<string, string>;

/** Every page, in the order of the hub: sports first, then organisers; by title within each. */
export const EVENT_PAGES: BlogPost[] = Object.entries(FILES)
  .map(([p, src]) => buildPost(p, src))
  .sort((a, b) => ((a.group ?? "") === (b.group ?? "") ? a.title.localeCompare(b.title) : (a.group ?? "") < (b.group ?? "") ? 1 : -1));

for (const p of EVENT_PAGES) {
  if (!p.group || !(p.group in EVENT_GROUPS)) throw new Error(`${p.slug}: group is ${Object.keys(EVENT_GROUPS).join(" or ")}`);
  for (const r of p.relatedPosts) if (!getPost(r)) throw new Error(`${p.slug}: relatedPosts has no blog post "${r}"`);
}

export const eventPagePath = (slug: string) => `${EVENT_PHOTOGRAPHY_PATH}/${slug}`;
export const getEventPage = (slug: string) => EVENT_PAGES.find((p) => p.slug === slug);

export const EVENT_INDEX_SEO = {
  title: "Event photography for sports and organisers | Photohawk",
  description:
    "Guides to sharing and selling event photos with Photohawk Studio: running, triathlon, cycling, trail, equestrian and obstacle events, and for organisers.",
};
